import { FlexBox, Text } from '@dixa/lib-shared-habla';
import { colors, effects, sizes } from '@dixa/lib-shared-tokens';
import type {
  ChartInstrument,
  InstrumentColor,
  InstrumentDataGranularity,
  MetricInstrument as MetricInstrumentT,
} from 'lib-analytics-types';
import { MetricInstrument } from 'lib-analytics-ui';
import moment from 'moment-timezone';
import { memo, useCallback, useMemo } from 'react';
import { TooltipProps } from 'recharts';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';

import { MetricInstrumentsGrid } from '../../../../renderer/components/metricInstrumentsGrid/metricInstrumentsGrid';
import { formatDateCategory, formatTextCategory } from '../../utils/formatting';

type ChartInstrumentTooltipProps = {
  /** The chart instrument. */
  instrument: ChartInstrument;
  /** The selected granularity. */
  granularity?: InstrumentDataGranularity;
  /** The Recharts tooltip props. */
  tooltipProps: TooltipProps<number, string>;
};

export const ChartInstrumentTooltip = memo(function ChartInstrumentTooltip({
  instrument,
  granularity,
  tooltipProps,
}: ChartInstrumentTooltipProps): JSX.Element | null {
  const {
    settings: { timezone },
  } = useAnalyticsState();

  const { active, payload, label } = tooltipProps;
  const { categories, series } = instrument.data;
  const category = categories.find((category) => category.label === label);
  const categoryMetrics = category?.metrics ?? [];

  const seriesLimit = 6 * 4; // 6 columns, 4 rows
  const seriesOverflow = Math.max(0, (payload?.length ?? 0) - seriesLimit);

  const seriesMetrics = useMemo(
    () =>
      payload
        ?.slice(
          0,
          // When there are more series than the limit, subtract one to make room for the overflow indicator.
          seriesOverflow > 0 ? seriesLimit - 1 : seriesLimit,
        )
        .map((item, index) => {
          const instrument: MetricInstrumentT = {
            type: 'metric',
            title: item.name ?? '',
            color: item.color as InstrumentColor,
            format: series[index].options?.format ?? 'number',
            data: item.value ?? 0,
          };

          return instrument;
        }),
    [payload, series, seriesLimit, seriesOverflow],
  );

  const renderMetricInstruments = useCallback(
    (instruments?: readonly MetricInstrumentT[]) =>
      instruments?.map((instrument) => (
        <MetricInstrument
          key={instrument.title}
          instrument={instrument}
          theme="dark"
        />
      )),
    [],
  );

  const categoryLabel = useMemo(
    () =>
      moment(label, moment.ISO_8601).isValid()
        ? formatDateCategory(label, granularity, { timezone, long: true })
        : formatTextCategory(label),
    [granularity, label, timezone],
  );

  if (!active || !payload) {
    return null;
  }

  return (
    <FlexBox
      padding="250"
      borderWidth="1px"
      background={colors.neutral['900']}
      boxShadow={effects['box-shadow']['300']}
      borderColor={colors.neutral['900']}
      borderRadius={sizes.border['200']}
      flexDirection="column"
      gap="300"
    >
      <FlexBox flexDirection="column" gap="100">
        {/* The label of the active category. */}
        <Text.Paragraph as="div" variant="1" color="white">
          {categoryLabel}
        </Text.Paragraph>

        {/* The optional description of the active category. */}
        {category?.description && (
          <Text.Paragraph variant="4" color="white">
            {category.description}
          </Text.Paragraph>
        )}
      </FlexBox>

      {/* The optional metrics of the active category. */}
      {categoryMetrics.length > 0 && (
        <MetricInstrumentsGrid bounded>
          {renderMetricInstruments(categoryMetrics)}
        </MetricInstrumentsGrid>
      )}

      {/* The series data for the active category. */}
      <MetricInstrumentsGrid bounded>
        {renderMetricInstruments(seriesMetrics)}

        {seriesOverflow > 0 && (
          <FlexBox flex="1" alignItems="center">
            <Text.Heading variant="heading-2" color="white">
              + {seriesOverflow + 1} more
            </Text.Heading>
          </FlexBox>
        )}
      </MetricInstrumentsGrid>
    </FlexBox>
  );
});
